import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useInRouterContext } from 'react-router-dom';
import { NotificationContainer } from '@/components/ui/notification';
import { notificationErrorHandler, type NotificationError } from '@/utils/notificationErrorHandler';
import { notificationSecurity } from '@/utils/notificationSecurity';
import { notificationQueue, type QueuedNotification } from '@/utils/notificationQueue';
import api from '@/lib/axios';
import { useAuth } from '@/contexts/AuthContext';
// import { notificationAnalytics } from '@shared/utils/notificationAnalytics';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
}

interface NotificationContextType {
  showNotification: (type: NotificationType, title: string, message?: string, options?: {
    priority?: number;
    persistent?: boolean;
    dedupKey?: string;
    url?: string;
  }) => void;
  showErrorNotification: (error: Error | NotificationError, fallbackMessage?: string) => void;
  clearAllNotifications: () => void;
  getQueueStatus: () => { queueLength: number; visibleCount: number; hasHighPriority: boolean };
  // Badge count management
  badgeCount: number;
  setBadgeCount: (count: number | ((prev: number) => number)) => void;
  decreaseBadgeCount: (amount?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Toast clicks navigate via the router, but the provider must not hard-require
// one (tests and storybook-style mounts render it bare). useInRouterContext is
// safe to call anywhere; only this routed variant touches useNavigate.
function RoutedNotificationContainer(props: Omit<React.ComponentProps<typeof NotificationContainer>, 'onNavigate'>) {
  const navigate = useNavigate();
  return <NotificationContainer {...props} onNavigate={navigate} />;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<QueuedNotification[]>([]);
  const [badgeCount, setBadgeCountState] = useState<number>(0);
  const updateIntervalRef = useRef<NodeJS.Timeout>();
  const { user } = useAuth();
  const inRouter = useInRouterContext();

  const removeNotification = useCallback((id: string) => {
    // Track dismissal analytics
    // notificationAnalytics.trackNotificationDismissed(id);

    notificationQueue.dequeue(id);
    updateNotifications();
  }, []);

  const updateNotifications = useCallback(() => {
    setNotifications(notificationQueue.getVisible());
  }, []);

  const showNotification = useCallback((
    type: NotificationType,
    title: string,
    message?: string,
    options?: {
      priority?: number;
      persistent?: boolean;
      dedupKey?: string;
      url?: string;
    }
  ) => {
    try {
      // Rate limiting check
      const userId = 'current-user'; // In real app, get from auth context
      if (!notificationSecurity.checkRateLimit(userId)) {
        console.warn('Notification rate limit exceeded');
        return;
      }

      // Validate and sanitize input
      const validation = notificationSecurity.validateNotificationData({
        type,
        title,
        message,
      });

      if (!validation.isValid || !validation.sanitizedValue) {
        console.error('Invalid notification data:', validation.errors);
        // Show a generic error notification instead
        notificationQueue.enqueue({
          type: 'error',
          title: 'Invalid Notification',
          message: 'The notification could not be displayed due to invalid content.',
          priority: 1,
        });
        updateNotifications();
        return;
      }

      const sanitizedData = validation.sanitizedValue;

      // Add to queue with options. Only in-app routes may be attached — a
      // toast must never navigate somewhere absolute/external.
      const safeUrl = options?.url && options.url.startsWith('/') ? options.url : undefined;
      const notificationId = notificationQueue.enqueue({
        type: sanitizedData.type,
        title: sanitizedData.title,
        message: sanitizedData.message,
        priority: options?.priority,
        persistent: options?.persistent,
        dedupKey: options?.dedupKey,
        url: safeUrl,
      });

      // Track analytics
      // notificationAnalytics.trackNotificationCreated({
      //   id: notificationId,
      //   type: sanitizedData.type,
      //   priority: options?.priority || 3,
      //   userId: 'current-user', // Get from auth context in real app
      //   storeId: 'current-store', // Get from auth context in real app
      //   deliveryChannel: 'inApp',
      //   userAgent: navigator.userAgent,
      //   sessionId: sessionStorage.getItem('sessionId') || 'unknown',
      // });

      // Mark as delivered immediately for in-app notifications
      // notificationAnalytics.trackNotificationDelivered(notificationId);

      updateNotifications();
    } catch (error) {
      // Fallback: try to show a basic error notification
      try {
        notificationQueue.forceShow({
          type: 'error',
          title: 'System Error',
          message: 'Unable to display notification',
        });
        updateNotifications();
      } catch (fallbackError) {
      }
    }
  }, [updateNotifications]);

  const showErrorNotification = useCallback((error: Error | NotificationError, fallbackMessage?: string) => {
    try {
      const notificationError = error as NotificationError;
      const userMessage = notificationError.code
        ? notificationErrorHandler.getUserFriendlyMessage(notificationError)
        : fallbackMessage || 'An error occurred';

      showNotification('error', 'Error', userMessage, {
        priority: 1,
        dedupKey: `error-${notificationError.code || 'generic'}`
      });
    } catch (displayError) {
      // Last resort fallback
      showNotification('error', 'System Error', fallbackMessage || 'Multiple errors occurred', { priority: 1 });
    }
  }, [showNotification]);

  const clearAllNotifications = useCallback(() => {
    notificationQueue.clear();
    updateNotifications();
  }, [updateNotifications]);

  const getQueueStatus = useCallback(() => {
    return notificationQueue.getStatus();
  }, []);

  // Badge count management
  const setBadgeCount = useCallback((updater: number | ((prev: number) => number)) => {
    setBadgeCountState(prev => {
      const next = typeof updater === 'function'
        ? (updater as (prev: number) => number)(prev)
        : updater;

      // Update app badge channels
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'UPDATE_BADGE_COUNT',
          count: next
        });
      }

      if ('setAppBadge' in navigator) {
        if (next > 0) {
          (navigator as any).setAppBadge(next);
        } else {
          (navigator as any).clearAppBadge();
        }
      }

      return next;
    });
  }, []);

  const decreaseBadgeCount = useCallback((amount: number = 1) => {
    setBadgeCount(prev => Math.max(0, prev - amount));
  }, [setBadgeCount]);

  // Fetch badge count from server (only when authenticated)
  const fetchBadgeCount = useCallback(async () => {
    // Don't fetch if user is not logged in
    if (!user) {
      return;
    }

    try {
      const response = await api.get('/api/notifications');
      const unreadCount = response.data.notifications.filter(
        (notification: any) => !notification.read
      ).length;
      setBadgeCount(unreadCount);
    } catch (error) {
      // avoid noisy console errors; the axios interceptor handles final Sentry
      // reporting after retries when the tab is visible.
      console.debug('Notification badge refresh failed; keeping previous count.', error);
    }
  }, [setBadgeCount, user]);

  // Setup periodic updates and cleanup
  useEffect(() => {
    // Update notifications from queue every 100ms for smooth UX
    updateIntervalRef.current = setInterval(() => {
      updateNotifications();
    }, 100);

    // Cleanup rate limits every 5 minutes
    const rateLimitCleanup = setInterval(() => {
      notificationSecurity.cleanupRateLimits();
    }, 5 * 60 * 1000);

    // Only fetch badge count if user is authenticated. Poll every 60s while the
    // tab is visible; hidden tabs (left open all day) skip the poll and catch up
    // the moment they're shown again.
    let badgeCountInterval: NodeJS.Timeout | undefined;
    const pollBadgeCount = () => {
      if (!document.hidden) fetchBadgeCount();
    };
    const handleVisibilityChange = () => {
      if (!document.hidden) fetchBadgeCount();
    };
    if (user) {
      // Initial fetch
      fetchBadgeCount();

      badgeCountInterval = setInterval(pollBadgeCount, 60000);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
      if (badgeCountInterval) {
        clearInterval(badgeCountInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      clearInterval(rateLimitCleanup);
    };
  }, [updateNotifications, fetchBadgeCount, user]);

  return (
    <NotificationContext.Provider value={{
      showNotification,
      showErrorNotification,
      clearAllNotifications,
      getQueueStatus,
      badgeCount,
      setBadgeCount,
      decreaseBadgeCount
    }}>
      {children}
      {inRouter ? (
        <RoutedNotificationContainer
          notifications={notifications}
          onClose={removeNotification}
        />
      ) : (
        <NotificationContainer
          notifications={notifications}
          onClose={removeNotification}
        />
      )}
    </NotificationContext.Provider>
  );
}

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}; 